import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, keyframes } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

const pinIn = keyframes`
  0% { transform: translateY(0px) rotate(-30deg); }
  35% { transform: translateY(-5px) rotate(-8deg); }
  65% { transform: translateY(3px) rotate(0deg) scale(1.03, 0.94); }
  82% { transform: translateY(0px) rotate(0deg); }
  100% { transform: translateY(1px) rotate(0deg); }
`;

const pinOut = keyframes`
  0% { transform: translateY(1px) rotate(0deg); }
  20% { transform: translateY(2px) rotate(-8deg) scale(0.97); }
  65% { transform: translateY(-5px) rotate(-30deg) scale(1.04); }
  100% { transform: translateY(0px) rotate(-30deg); }
`;

const PinButton = React.forwardRef(function PinButton(
  { pinned = false, disabled = false, size = "medium", sx, ...props },
  ref
) {
  const previousPinned = useRef(pinned);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (previousPinned.current !== pinned) {
      previousPinned.current = pinned;
      setHasChanged(true);
    }
  }, [pinned]);

  const tooltip = pinned
    ? "Pinned to top. Click to unpin."
    : "Not pinned. Click to pin to top.";

  return (
    <Tooltip title={tooltip} describeChild arrow>
      <Box component='span' sx={{ display: "inline-flex" }}>
        <IconButton
          {...props}
          ref={ref}
          color='primary'
          size={size}
          disabled={disabled}
          aria-label='Pin header to top'
          aria-pressed={pinned}
          sx={[
            (theme) => ({
              borderRadius: "8px",

              bgcolor: pinned
                ? alpha(theme.palette.primary.main, 0.1)
                : theme.palette.action.hover,

              color: pinned ? "primary.main" : "text.secondary",

              transition:
                "transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1), background-color 180ms ease, color 180ms ease",

              ...(!disabled && {
                "@media (hover: hover)": {
                  "&:hover": {
                    bgcolor: alpha(
                      theme.palette.primary.main,
                      pinned ? 0.16 : 0.08
                    ),
                    color: "primary.main",
                  },
                },

                "&:active": {
                  transform: "scale(0.97, 0.94)",
                  transition:
                    "transform 90ms ease-out, background-color 180ms ease, color 180ms ease",
                },
              }),

              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "text.disabled",
              },

              "&.Mui-focusVisible": {
                outline: `2px solid ${alpha(theme.palette.primary.main, 0.65)}`,
                outlineOffset: 3,
              },

              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:active": {
                  transform: "none",
                },
              },
            }),
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <Box
            component='span'
            aria-hidden='true'
            sx={{
              display: "inline-flex",
              width: "1em",
              height: "1em",
              flexShrink: 0,
              fontSize: "inherit",
              transform: pinned
                ? "translateY(1px) rotate(0deg)"
                : "translateY(0px) rotate(-30deg)",
              transformOrigin: "50% 50%",
              animation: hasChanged
                ? `${pinned ? pinIn : pinOut} 360ms ease-out`
                : "none",
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {pinned ? (
              <PushPinIcon fontSize='inherit' />
            ) : (
              <PushPinOutlinedIcon fontSize='inherit' />
            )}
          </Box>
        </IconButton>
      </Box>
    </Tooltip>
  );
});

export default PinButton;
